import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import React from "react";

const CELL_WIDTH = "150px"; // Fixed width for all columns

const CustomTable = ({
  columns,
  data,
  onRowClick,
  actions = [],
  page,
  rowsPerPage,
  totalCount,
  onPageChange,
  onRowsPerPageChange,
}) => {
  const theme = useTheme();

  return (
    <Box sx={{ position: "relative" }}>
      <TableContainer
        sx={{
          overflowX: "auto",
          boxShadow: 5,
          borderRadius: "12px",
          padding: 1,
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.default
              : "#fafafa",
        }}
      >
        <Table sx={{ minWidth: 650 }}>
          {/* Table Header */}
          <TableHead
            sx={{
              border: `2px solid ${
                theme.palette.mode === "dark"
                  ? theme.palette.primary.dark
                  : theme.palette.primary.main
              }`,
            }}
          >
            <TableRow>
              <TableCell
                align="center"
                sx={{
                  width: CELL_WIDTH,
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.primary.dark
                      : theme.palette.primary.main,
                  fontWeight: "bold",
                }}
              >
                No
              </TableCell>
              {columns.map((column) => (
                <TableCell
                  key={column.field}
                  align={column.align || "left"}
                  sx={{
                    width: CELL_WIDTH,
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.dark
                        : theme.palette.primary.main,
                    fontWeight: "bold",
                    textTransform: "capitalize",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  <Box>
                    {/* Header title */}
                    <Box
                      sx={{
                        fontWeight: "bold",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={column.headerName}
                    >
                      {column.headerName}
                    </Box>

                    {/* Small line below header */}
                    {column.subHeader && (
                      <Box
                        sx={{
                          fontSize: "10px",
                          color: theme.palette.text.secondary,
                          textTransform: "none",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          marginTop: "2px",
                        }}
                        title={column.subHeader}
                      >
                        {column.subHeader}
                      </Box>
                    )}
                  </Box>
                </TableCell>
              ))}
              {actions.length > 0 && (
                <TableCell
                  align="center"
                  sx={{
                    width: CELL_WIDTH,
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.primary.dark
                        : theme.palette.primary.main,
                    fontWeight: "bold",
                  }}
                >
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          {/* Table Body */}
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow
                key={row.id || rowIndex}
                hover
                onClick={() => onRowClick && onRowClick(row)}
                sx={{
                  cursor: onRowClick ? "pointer" : "default",
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                  },
                }}
              >
                <TableCell align="center" sx={{ width: CELL_WIDTH }}>
                  {page * rowsPerPage + rowIndex + 1}
                </TableCell>
                {columns.map((column) => (
                  <TableCell
                    key={column.field}
                    align={column.align || "left"}
                    sx={{ width: CELL_WIDTH }}
                  >
                    {column.renderCell ? (
                      column.renderCell(row[column.field], row)
                    ) : (
                      <Box
                        title={row[column.field]}
                        sx={{
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          maxWidth: CELL_WIDTH,
                          display: "inline-block",
                        }}
                      >
                        {row[column.field]}
                      </Box>
                    )}
                  </TableCell>
                ))}
                {actions.length > 0 && (
                  <TableCell align="center" sx={{ width: CELL_WIDTH }}>
                    {actions.map((action, actionIndex) => (
                      <IconButton
                        key={actionIndex}
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick(row, e);
                        }}
                        onMouseEnter={(e) => action.onMouseEnter && action.onMouseEnter(row, e)}
                        onMouseLeave={(e) => action.onMouseLeave && action.onMouseLeave(row, e)}
                      >
                        {React.createElement(action.Component, action.props)}
                      </IconButton>
                    ))}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination & Controls */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "5px",
          alignItems: "center",
          padding: "16px 8px",
          boxShadow: 5,
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.background.paper
              : "#f5f5f5",
        }}
      >
        <Typography variant="body2">
          Page {page + 1} of {Math.ceil(totalCount / rowsPerPage)}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={page === 0}
            onClick={(e) => onPageChange(e, page - 1)}
          >
            Previous
          </Button>
          <Button
            variant="contained"
            size="small"
            color="primary"
            disabled={page >= Math.ceil(totalCount / rowsPerPage) - 1}
            onClick={(e) => onPageChange(e, page + 1)}
          >
            Next
          </Button>
        </Box>

        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25]}
        />
      </Box>
    </Box>
  );
};

export default CustomTable;
